// frontend/src/hooks/useUndo.js
// Brighella v2 (ADR-014 D12) — Universal undo orchestration hook.
//
// One responsibility: take the latest revision id from useUndoStore,
// POST /api/revisions/:id/undo, and surface success / failure via the
// shared toast system. Listens for Cmd+Z (Mac) / Ctrl+Z (others) at the
// window level so any surface inherits the shortcut automatically.

import { useCallback, useEffect } from 'react'
import { useUndoStore, type UndoRevision } from '../store/undoStore'
import api from '../utils/api.js'

/** Optional callbacks fired after an undo attempt resolves. */
export interface UseUndoOptions {
  onSuccess?: (rev: UndoRevision) => void
  onError?: (err: unknown, rev: UndoRevision) => void
}

/** Return shape of {@link useUndo}. */
export interface UseUndoReturn {
  /** Pops the latest revision and POSTs its undo. Resolves true on success. */
  undoNow: () => Promise<boolean>
  /** The current latest revision, or null. */
  latest: UndoRevision | null
}

export function useUndo({ onSuccess, onError }: UseUndoOptions = {}): UseUndoReturn {
  const popLatest = useUndoStore((s) => s.popLatest)
  const latest    = useUndoStore((s) => s.latest)

  const undoNow = useCallback(async (): Promise<boolean> => {
    const rev = popLatest()
    if (!rev) return false
    try {
      await api.post(`/revisions/${rev.id}/undo`)
      onSuccess?.(rev)
      return true
    } catch (err) {
      onError?.(err, rev)
      return false
    }
  }, [popLatest, onSuccess, onError])

  // Global Cmd/Ctrl+Z. Ignored when focus is in an editable element so the
  // browser's native text undo still works in inputs and textareas.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey
      if (!meta || e.shiftKey || e.altKey) return
      if (e.key !== 'z' && e.key !== 'Z') return
      const t = e.target as HTMLElement | null
      const tag = t?.tagName
      const editable = (
        tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' ||
        t?.isContentEditable
      )
      if (editable) return
      if (!useUndoStore.getState().latest) return
      e.preventDefault()
      undoNow()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [undoNow])

  return { undoNow, latest }
}
